import { TextField } from "@cmsgov/ds-medicare-gov";
import { ChangeEventHandler, ComponentProps, useEffect, useState } from "react";
import translate, { TranslationKey } from "../../functions/translate";
import { Guideline } from "../Guideline";
import { GuidelineContainer } from "../GuidelineContainer";
import { getUsernameErrorMessage, getUsernameErrors } from "./usernameSchema";

type TextFieldProps = ComponentProps<typeof TextField>;
type OptionalProps = Partial<Pick<TextFieldProps, 'label'>>
type NewUsernameProps = Pick<TextFieldProps, 'inputRef' | 'fieldClassName'> & OptionalProps & {
    onUsernameError: (hasError: boolean) => void
    submitAttempted: boolean
    onUsernameChange: (username: string) => void
    apiErrorKey?: 'pleaseEnterUsername'
    component: 'yourAccountDetails' | 'updateAccountSection'
}

export const NewUsername = (props: NewUsernameProps) => {

    const [username, setUsername] = useState('');
    const [hasValidLengthAndNoSpaces, setHasValidLengthAndNoSpaces] = useState(false);
    const [hasValidSymbols, setHasValidSymbols] = useState(false);
    const [containsNoMbi, setContainsNoMbi] = useState(false);
    const [usernameTouched, setUsernameTouched] = useState(false);
    const [inlineError, setInlineError] = useState<TranslationKey | null>(null);

    const { onUsernameError, submitAttempted, apiErrorKey, component } = props;
    useEffect(() => {
        const usernameErrors = getUsernameErrors(username);

        const isNotEmpty = Boolean(username.length);

        const validLengthAndSpaces = isNotEmpty && !usernameErrors.has('invalidLengthOrSpace');
        const validSymbols = isNotEmpty && !usernameErrors.has('invalidSymbol');
        const noMbi = isNotEmpty && !usernameErrors.has('containsMBI');

        setHasValidLengthAndNoSpaces(validLengthAndSpaces);
        setHasValidSymbols(validSymbols);
        setContainsNoMbi(noMbi);

        const isValid = usernameErrors.size === 0 && isNotEmpty;
        if (onUsernameError) {
            onUsernameError(!isValid);
        }

        if (!usernameTouched && !submitAttempted) {
            return;
        }

        if (usernameErrors.size > 1) {
            if (isNotEmpty) {
                setInlineError('check_username_and_update');
            } else if (component === 'yourAccountDetails') {
                setInlineError('create_username_for_your_account');
            } else {
                setInlineError('enter_your_new_username');
            }
        } else if (usernameErrors.size === 1) {
            const err = [...usernameErrors.values()][0];
            setInlineError(getUsernameErrorMessage(err));
        } else if (apiErrorKey === 'pleaseEnterUsername') {
            setInlineError('create_username_for_your_account');
        } else {
            setInlineError(null);
        }

    }, [username, onUsernameError, submitAttempted, usernameTouched, apiErrorKey, component]);

    const guidelines = <GuidelineContainer>
        <Guideline text="must_be_eight_thirty_char_long_with_4_letters_without_space" isValid={hasValidLengthAndNoSpaces} />
        <Guideline text="allowed_symbol" isValid={hasValidSymbols} />
        <Guideline text="not_include_medicare_number" isValid={containsNoMbi} />
    </GuidelineContainer>

    const onUsernameChange: ChangeEventHandler<HTMLInputElement> = e => {
        const newUsername = e.target.value;
        setUsername(newUsername);
        props.onUsernameChange(newUsername);
    }

    const translatedInlineError = inlineError ? translate(inlineError) : null;

    return <>
        <div className="ds-u-measure--base ds-u-font-size--base ds-u-margin-top--2">
            {translate("your_user_not_email")}
        </div>
        <TextField
            label={props.label || translate('username')}
            labelClassName="ds-u-margin-top--1 ds-u-measure--wide" // prevent long guidelines from wrapping
            fieldClassName={props.fieldClassName}
            errorMessage={translatedInlineError}
            name="username"
            inputRef={props.inputRef}
            onBlur={() => setUsernameTouched(true)}
            hint={guidelines}
            value={username}
            onChange={onUsernameChange}
        />
    </>;
};